import React, { useState, useEffect } from 'react'
import Alert from '../../common/Alert'
import vehicleDailyTripService from '../../../services/vehicleDailyTripService'
import { X } from 'lucide-react'
import { validators, inputFilters, useValidation, ValidationMessage } from '../../common/Validation.js'import { useTranslation } from 'react-i18next';


const EditVehicleDailyTripModal = ({ tripId, onSuccess, onClose }) => {
  const [submitting, setSubmitting] = useState(false)
  const [loading, setLoading] = useState(true)
  const [formData, setFormData] = useState({
    trip_date: '',
    opening_km: '',
    closing_km: '',
    delivery_point: '',
    toll: '',
    in_time: '',
    out_time: '',
    driver: ''
  })

  const { errors, validate, validateField } = useValidation()

  const validationRules = {
    trip_date:   (v) => validators.required(v, 'Trip date'),
    opening_km:  (v) => validators.required(v, 'Opening KM') || validators.number(v, { min: 0, label: 'Opening KM' }),
    closing_km:  (v) => validators.required(v, 'Closing KM') || validators.number(v, { min: 0, label: 'Closing KM' }),
    toll:        (v) => validators.number(v, { min: 0, label: 'Toll' }),
    driver:      (v) => validators.textOnly(v, 'Driver'),
  }

  useEffect(() => {
    fetchTrip()
  }, [tripId])

  const fetchTrip = async () => {
    try {
      setLoading(true)
      const response = await vehicleDailyTripService.getOne(tripId)
      const trip = response.data.data

      setFormData({
        trip_date: trip.trip_date,
        opening_km: trip.opening_km,
        closing_km: trip.closing_km,
        delivery_point: trip.delivery_point || '',
        toll: trip.toll || '',
        in_time: trip.in_time || '',
        out_time: trip.out_time || '',
        driver: trip.driver || ''
      })
    } catch (err) {
      Alert.error('Failed to load trip details')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target

    let filtered = value
    if (['opening_km', 'closing_km', 'toll'].includes(name)) filtered = inputFilters.amount(value)
    else if (name === 'driver') filtered = inputFilters.lettersOnly(value)

    setFormData(prev => ({ ...prev, [name]: filtered }))

    if (validationRules[name]) {
      validateField(name, filtered, validationRules[name])
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validate(formData, validationRules)) {
      Alert.error('Please fix the highlighted errors before submitting')
      return
    }

    if (formData.opening_km && formData.closing_km && parseFloat(formData.closing_km) <= parseFloat(formData.opening_km)) {
      Alert.error('Closing KM must be greater than Opening KM')
      return
    }

    try {
      setSubmitting(true)
      await vehicleDailyTripService.update(tripId, {
        ...formData,
        opening_km: parseFloat(formData.opening_km),
        closing_km: parseFloat(formData.closing_km),
        toll: formData.toll ? parseFloat(formData.toll) : 0
      })

      Alert.success('Daily trip updated successfully!')
      onSuccess()
      onClose()
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to update daily trip')
      console.error(err)
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-lg p-8 w-full max-w-2xl">
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] overflow-auto">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white">
          <h2 className="text-xl font-bold text-secondary">Edit Daily Trip</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="grid grid-cols-2 sm:grid-cols-2 gap-2 sm:gap-6">
            {/* Trip Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Trip Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="trip_date"
                value={formData.trip_date}
                onChange={handleChange}
                className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-blue-400 ${
                  errors.trip_date ? 'border-red-500' : 'border-gray-200'
                }`}
              />
              <ValidationMessage error={errors.trip_date} />
            </div>

            {/* Opening KM */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Opening KM <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="opening_km"
                value={formData.opening_km}
                onChange={handleChange}
                placeholder="0.00"
                className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-blue-400 ${
                  errors.opening_km ? 'border-red-500' : 'border-gray-200'
                }`}
              />
              <ValidationMessage error={errors.opening_km} />
            </div>

            {/* Closing KM */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Closing KM <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="closing_km"
                value={formData.closing_km}
                onChange={handleChange}
                placeholder="0.00"
                className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-blue-400 ${
                  errors.closing_km ? 'border-red-500' : 'border-gray-200'
                }`}
              />
              <ValidationMessage error={errors.closing_km} />
            </div>

            {/* Delivery Point */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Delivery Point
              </label>
              <input
                type="text"
                name="delivery_point"
                value={formData.delivery_point}
                onChange={handleChange}
                placeholder="Enter delivery point"
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400"
              />
            </div>

            {/* In Time */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                In Time
              </label>
              <input
                type="time"
                name="in_time"
                value={formData.in_time}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400"
              />
            </div>

            {/* Out Time */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Out Time
              </label>
              <input
                type="time"
                name="out_time"
                value={formData.out_time}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400"
              />
            </div>

            {/* Toll */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Toll (₹)
              </label>
              <input
                type="text"
                name="toll"
                value={formData.toll}
                onChange={handleChange}
                placeholder="0.00"
                className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-blue-400 ${
                  errors.toll ? 'border-red-500' : 'border-gray-200'
                }`}
              />
              <ValidationMessage error={errors.toll} />
            </div>

            {/* Driver */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Driver
              </label>
              <input
                type="text"
                name="driver"
                value={formData.driver}
                onChange={handleChange}
                placeholder="Enter driver name"
                className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-blue-400 ${
                  errors.driver ? 'border-red-500' : 'border-gray-200'
                }`}
              />
              <ValidationMessage error={errors.driver} />
            </div>
          </div>

          {/* Modal Actions */}
          <div className="mt-8 flex gap-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-2 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 px-6 py-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white rounded-lg transition"
            >
              {submitting ? 'Updating...' : 'Update Trip'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default EditVehicleDailyTripModal
